import React, { useState, useCallback, useMemo } from 'react';
import StepSelect from './StepSelect';
import StepInterface from './StepInterface';
import StepBridge from './StepBridge';
import StepInternet from './StepInternet';
import StepVLAN from './StepVLAN';
import StepDHCP from './StepDHCP';
import StepFirewall from './StepFirewall';
import StepPortForward from './StepPortForward';
import StepHotspot from './StepHotspot';
import StepLoadBalance from './StepLoadBalance';
import StepQoS from './StepQoS';
import StepTimeNTP from './StepTimeNTP';
import StepLog from './StepLog';
import StepSecurity from './StepSecurity';
import StepVPN from './StepVPN';
import StepWebsiteBlock from './StepWebsiteBlock';
import StepReview from './StepReview';
import StepOutput from './StepOutput';
import NetworkDiagram from '../Output/NetworkDiagram';
import { MODULES, getStepTitle, getStepIcon } from '../../data/modules';

const DEFAULT_INTERFACES = {
  interfaces: [
    { id: 'ether1', name: 'ether1', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
    { id: 'ether2', name: 'ether2', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
    { id: 'ether3', name: 'ether3', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
    { id: 'ether4', name: 'ether4', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
    { id: 'ether5', name: 'ether5', type: 'ether', comment: '', enabled: true, mtu: 1500, disabled: false },
    { id: 'sfp1', name: 'sfp-sfpplus1', type: 'sfp', comment: '', enabled: true, mtu: 1500, disabled: false },
  ],
};

const DEFAULT_INTERNET = {
  wans: [{
    type: 'pppoe',
    wan_interface: 'ether1',
    interface_name: 'pppoe-out1',
    username: '',
    password: '',
    ip_address: '',
    gateway: '',
    use_peer_dns: true,
    add_default_route: true,
    route_distance: '1',
    comment: 'WAN-1',
  }],
  dns: '8.8.8.8,8.8.4.4',
  allow_remote_dns: true,
  lan_network: '192.168.88.0/24',
};

const INITIAL_STATE = {
  selectedModules: [],
  interfaces: DEFAULT_INTERFACES,
  bridges: [],
  internet: DEFAULT_INTERNET,
  vlans: [],
  dhcpServers: [],
  firewall: { drop_invalid: true, default_action: 'drop', anti_spoof: false, rules: [] },
  portForwards: [],
  hotspot: { enabled: false },
  loadBalance: { method: 'pcc', check_gateway: true, wan_interfaces: [] },
  qos: { rules: [] },
  timeNtp: { timezone: 'Asia/Bangkok', ntp_enabled: true, ntp_servers: ['1.th.pool.ntp.org', '2.th.pool.ntp.org', '3.th.pool.ntp.org'], ntp_interval: '1h', custom_ntp: '' },
  logging: { enabled: true, storage: 'local', local_disk: true, local_memory: false, memory_size: '10M', remote_enabled: false, remote_server: '', remote_port: 514, remote_protocol: 'udp', remote_facility: 'local7', log_topics: { firewall: true, connection: true, dhcp: true, hotspot: false, system: true, interface: false, routing: false, wireless: false }, log_action: true, log_prefix: true },
  security: { disable_services: { api: true, ftp: true, telnet: true, www: true, winbox: false, ssh: false }, custom_ports: { winbox: 8291, ssh: 22, api: 8728, www: 80 }, brute_force: { enabled: true, max_attempts: 3, ban_time: '10m', detect_time: '5m', whitelist: '192.168.88.0/24' }, hardening: { disable_ipv6: false, disable_ipv6_firewall: true, disable_neighbor_discovery: false, disable_macs_server: true, disable_upnp: true, disable_cloud: true, secure_mode: false } },
  vpn: { server: { type: 'l2tp', name: 'l2tp-server', ip_pool: 'vpn-pool', local_address: '10.10.10.1', remote_address: '10.10.10.100-10.10.10.200', dns_server: '8.8.8.8', mtu: 1450, mru: 1450, use_encryption: true, require_peer_encryption: false, keepalive_timeout: '30s', comment: 'VPN Server' }, users: [{ name: 'vpnuser', password: 'vpn1234', service: 'l2tp', profile: 'default' }], enabled: true },
  websiteBlock: { enabled: true, sites: [], block_method: 'layer7' },
};

const STEP_ORDER = [
  'select', 'internet', 'vlans', 'dhcp', 'firewall',
  'portforward', 'hotspot', 'loadbalance', 'qos', 'review', 'output'
];

export default function Wizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [config, setConfig] = useState(INITIAL_STATE);
  const [generatedScript, setGeneratedScript] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [showDiagram, setShowDiagram] = useState(true);

  const activeSteps = ['select'];
  if (config.selectedModules.includes('interface')) activeSteps.push('interface');
  if (config.selectedModules.includes('bridge')) activeSteps.push('bridge');
  if (config.selectedModules.includes('internet')) activeSteps.push('internet');
  if (config.selectedModules.includes('vlans')) activeSteps.push('vlans');
  if (config.selectedModules.includes('dhcp')) activeSteps.push('dhcp');
  if (config.selectedModules.includes('timentp')) activeSteps.push('timentp');
  if (config.selectedModules.includes('firewall')) activeSteps.push('firewall');
  if (config.selectedModules.includes('vpn')) activeSteps.push('vpn');
  if (config.selectedModules.includes('portforward')) activeSteps.push('portforward');
  if (config.selectedModules.includes('security')) activeSteps.push('security');
  if (config.selectedModules.includes('qos')) activeSteps.push('qos');
  if (config.selectedModules.includes('hotspot')) activeSteps.push('hotspot');
  if (config.selectedModules.includes('loadbalance')) activeSteps.push('loadbalance');
  if (config.selectedModules.includes('logging')) activeSteps.push('logging');
  if (config.selectedModules.includes('websiteblock')) activeSteps.push('websiteblock');
  activeSteps.push('review', 'output');

  const currentStepKey = activeSteps[currentStep] || 'select';
  const totalSteps = activeSteps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const interfaceNames = useMemo(() => {
    const physical = (config.interfaces?.interfaces || []).map(iface => iface.name);
    const bridges = (config.bridges || []).map(b => b.name).filter(n => n);
    return [...new Set([...physical, ...bridges])];
  }, [config.interfaces, config.bridges]);

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) setCurrentStep(s => s + 1);
  }, [currentStep, totalSteps]);

  const goBack = useCallback(() => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  }, [currentStep]);

  const goTo = useCallback((step) => setCurrentStep(step), []);

  const updateConfig = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError('');

    const payload = buildPayload(config);
    const script = generateLocal(payload);

    // Try API first for enhanced generation, fallback to local silently
    try {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(3000),
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedScript(data.script);
        goNext();
        setIsGenerating(false);
        return;
      }
    } catch (err) {
      // Silently use local generation
    }

    setGeneratedScript(script);
    goNext();
    setIsGenerating(false);
  }, [config, goNext]);

  const handleReset = useCallback(() => {
    setConfig(INITIAL_STATE);
    setCurrentStep(0);
    setGeneratedScript('');
    setError('');
  }, []);

  const handleImport = useCallback((json) => {
    try {
      const data = typeof json === 'string' ? JSON.parse(json) : json;
      setConfig({ ...INITIAL_STATE, ...data });
    } catch (e) {
      setError('รูปแบบ JSON ไม่ถูกต้อง');
    }
  }, []);

  const handleExport = useCallback(() => {
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mikrotik-config.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [config]);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Sidebar - Step Navigation */}
      <aside className="lg:w-72 xl:w-80 flex-shrink-0">
        <div className="card p-4 lg:sticky lg:top-6">
          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">ความคืบหน้า</span>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Steps List */}
          <nav className="space-y-1">
            {activeSteps.map((step, i) => {
              const isActive = i === currentStep;
              const isDone = i < currentStep;
              const icon = getStepIcon(step);
              const title = getStepTitle(step);

              return (
                <button
                  key={step}
                  onClick={() => goTo(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all text-sm ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold ring-1 ring-blue-200 dark:ring-blue-800'
                      : isDone
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                      : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-md'
                      : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }`}>
                    {isDone ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    ) : (
                      <span>{icon}</span>
                    )}
                  </div>
                  <span className="truncate">{title}</span>
                </button>
              );
            })}
          </nav>

          {/* Network Diagram Toggle */}
          {currentStepKey !== 'output' && currentStepKey !== 'select' && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowDiagram(!showDiagram)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <span>แผนผังเครือข่าย</span>
                <svg className={`w-4 h-4 transition-transform ${showDiagram ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm animate-fadeIn flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">ปิด</button>
          </div>
        )}

        {/* Step Content */}
        <div className="card p-6 animate-fadeIn">
          {currentStepKey === 'select' && (
            <StepSelect config={config} updateConfig={updateConfig} onNext={goNext} />
          )}
          {currentStepKey === 'interface' && (
            <StepInterface data={config.interfaces} onChange={(v) => updateConfig('interfaces', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'bridge' && (
            <StepBridge data={config.bridges} onChange={(v) => updateConfig('bridges', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'internet' && (
            <StepInternet data={config.internet} onChange={(v) => updateConfig('internet', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'vlans' && (
            <StepVLAN data={config.vlans} onChange={(v) => updateConfig('vlans', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'dhcp' && (
            <StepDHCP data={config.dhcpServers} onChange={(v) => updateConfig('dhcpServers', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'firewall' && (
            <StepFirewall data={config.firewall} onChange={(v) => updateConfig('firewall', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'vpn' && (
            <StepVPN data={config.vpn} onChange={(v) => updateConfig('vpn', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'portforward' && (
            <StepPortForward data={config.portForwards} onChange={(v) => updateConfig('portForwards', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'portforward' && (
            <StepPortForward data={config.portForwards} onChange={(v) => updateConfig('portForwards', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'hotspot' && (
            <StepHotspot data={config.hotspot} onChange={(v) => updateConfig('hotspot', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'loadbalance' && (
            <StepLoadBalance data={config.loadBalance} onChange={(v) => updateConfig('loadBalance', v)} onNext={goNext} onBack={goBack} interfaceNames={interfaceNames} />
          )}
          {currentStepKey === 'qos' && (
            <StepQoS data={config.qos} onChange={(v) => updateConfig('qos', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'timentp' && (
            <StepTimeNTP data={config.timeNtp} onChange={(v) => updateConfig('timeNtp', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'logging' && (
            <StepLog data={config.logging} onChange={(v) => updateConfig('logging', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'websiteblock' && (
            <StepWebsiteBlock data={config.websiteBlock} onChange={(v) => updateConfig('websiteBlock', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'security' && (
            <StepSecurity data={config.security} onChange={(v) => updateConfig('security', v)} onNext={goNext} onBack={goBack} />
          )}
          {currentStepKey === 'review' && (
            <StepReview config={config} onBack={goBack} onGenerate={handleGenerate} isGenerating={isGenerating} onImport={handleImport} onExport={handleExport} />
          )}
          {currentStepKey === 'output' && (
            <StepOutput script={generatedScript} onBack={() => goTo(totalSteps - 2)} onReset={handleReset} />
          )}
        </div>

        {/* Network Diagram Preview */}
        {showDiagram && currentStepKey !== 'output' && currentStepKey !== 'select' && (
          <div className="card p-4 mt-4 animate-slideIn">
            <NetworkDiagram config={config} />
          </div>
        )}
      </main>
    </div>
  );
}

function buildPayload(config) {
  const payload = {};
  if (config.selectedModules.includes('interface') && config.interfaces) payload.interfaces = config.interfaces;
  if (config.selectedModules.includes('bridge')) payload.bridges = config.bridges || [];
  if (config.selectedModules.includes('internet') && config.internet) payload.internet = config.internet;
  if (config.selectedModules.includes('vlans') && config.vlans.length > 0) payload.vlans = config.vlans;
  if (config.selectedModules.includes('dhcp') && config.dhcpServers.length > 0) payload.dhcp_servers = config.dhcpServers;
  if (config.selectedModules.includes('firewall') && config.firewall) payload.firewall = config.firewall;
  if (config.selectedModules.includes('portforward') && config.portForwards.length > 0) payload.port_forwards = config.portForwards;
  if (config.selectedModules.includes('hotspot') && config.hotspot) payload.hotspot = config.hotspot;
  if (config.selectedModules.includes('loadbalance') && config.loadBalance) payload.load_balance = config.loadBalance;
  if (config.selectedModules.includes('qos') && config.qos) payload.qos = config.qos;
  if (config.selectedModules.includes('timentp') && config.timeNtp) payload.time_ntp = config.timeNtp;
  if (config.selectedModules.includes('logging') && config.logging) payload.logging = config.logging;
  if (config.selectedModules.includes('security') && config.security) payload.security = config.security;
  if (config.selectedModules.includes('vpn') && config.vpn) payload.vpn = config.vpn;
  if (config.selectedModules.includes('portforward') && config.portForwards.length > 0) payload.port_forwards = config.portForwards;
  if (config.selectedModules.includes('websiteblock') && config.websiteBlock) payload.website_block = config.websiteBlock;
  return payload;
}

function getWanTypeCommands(wan, index) {
  const lines = [];
  const t = wan.type;
  const name = wan.interface_name || autoIfaceName(t, index);

  if (t === 'pppoe') {
    let cmd = `/interface pppoe-client add name=${name} interface=${wan.wan_interface || 'ether1'}`;
    if (wan.username) cmd += ` user="${wan.username}"`;
    if (wan.password) cmd += ` password="${wan.password}"`;
    if (wan.add_default_route) cmd += ` add-default-route=yes default-route-distance=${wan.route_distance || '1'}`;
    cmd += ' disabled=no';
    lines.push(cmd);
  } else if (t === 'dhcp_client') {
    let cmd = `/ip dhcp-client add interface=${wan.wan_interface || 'ether1'}`;
    if (wan.add_default_route) cmd += ` add-default-route=yes default-route-distance=${wan.route_distance || '1'}`;
    if (wan.use_peer_dns) cmd += ' use-peer-dns=yes';
    cmd += ' disabled=no';
    lines.push(cmd);
  } else if (t === 'static') {
    if (wan.ip_address) {
      lines.push(`/ip address add address=${wan.ip_address} interface=${wan.wan_interface || 'ether1'} disabled=no`);
    } else {
      lines.push(`# ⚠️ กรุณากรอก IP Address สำหรับ ${name}`);
    }
    if (wan.gateway && wan.add_default_route) {
      lines.push(`/ip route add dst-address=0.0.0.0/0 gateway=${wan.gateway} distance=${wan.route_distance || '1'}`);
    } else if (wan.add_default_route && !wan.gateway) {
      lines.push(`# ⚠️ กรุณากรอก Gateway สำหรับ ${name}`);
    }
  } else if (t === 'pptp_client') {
    lines.push(`/interface pptp-client add name=${name} connect-to="${wan.username}" user="${wan.password}" password="${wan.ip_address}" add-default-route=${wan.add_default_route ? 'yes' : 'no'} default-route-distance=${wan.route_distance || '1'} disabled=no`);
  } else if (t === 'l2tp_client') {
    lines.push(`/interface l2tp-client add name=${name} connect-to="${wan.username}" user="${wan.password}" password="${wan.ip_address}" use-ipsec=yes ipsec-secret="${wan.gateway || ''}" add-default-route=${wan.add_default_route ? 'yes' : 'no'} default-route-distance=${wan.route_distance || '1'} disabled=no`);
  } else if (t === 'sstp_client') {
    lines.push(`/interface sstp-client add name=${name} connect-to="${wan.username}" user="${wan.password}" password="${wan.ip_address}" add-default-route=${wan.add_default_route ? 'yes' : 'no'} default-route-distance=${wan.route_distance || '1'} disabled=no`);
  } else if (t === 'ovpn_client') {
    let cmd = `/interface ovpn-client add name=${name} connect-to="${wan.username}" user="${wan.password}" password="${wan.ip_address}"`;
    if (wan.gateway) cmd += ` certificate=${wan.gateway}`;
    cmd += ` add-default-route=${wan.add_default_route ? 'yes' : 'no'} disabled=no`;
    lines.push(cmd);
  }
  return lines;
}

function autoIfaceName(type, index) {
  const map = { pppoe: 'pppoe-out', pptp_client: 'pptp-out', l2tp_client: 'l2tp-out', sstp_client: 'sstp-out', ovpn_client: 'ovpn-out', dhcp_client: 'dhcp', static: 'ether' };
  return (map[type] || 'wan') + (index + 1);
}

function generateLocal(payload) {
  const lines = [
    '# ============================================================',
    '# สคริปต์ตั้งค่า MikroTik',
    '# สร้างโดย MikroTik Config Wizard',
    '# วันที่: ' + new Date().toLocaleString('th-TH'),
    '# ============================================================',
    '',
  ];

  if (payload.internet) {
    const inet = payload.internet;
    const wans = inet.wans || [];
    wans.forEach((wan, i) => {
      const name = wan.interface_name || autoIfaceName(wan.type, i);
      const comment = wan.comment || ('WAN-' + (i + 1));
      lines.push(`# --- ${comment} (${wan.type.toUpperCase()}) ---`);
      lines.push(...getWanTypeCommands(wan, i));
      lines.push('');
    });
    if (inet.dns) {
      lines.push('# --- DNS ---');
      lines.push(`/ip dns set servers=${inet.dns}`);
      if (inet.allow_remote_dns) lines.push('/ip dns set allow-remote-requests=yes');
      lines.push('');
    }
  }

  if (payload.interfaces) {
    const ifaces = payload.interfaces.interfaces || [];
    if (ifaces.length > 0) {
      lines.push('# --- Interface Configuration ---');
      ifaces.forEach(iface => {
        const origName = iface.id || iface.name;
        const newName = iface.name;
        if (origName !== newName) {
          lines.push(`/interface set [find default-name=${origName}] name=${newName}`);
        }
        if (iface.mtu && iface.mtu !== 1500) {
          lines.push(`/interface set ${newName} mtu=${iface.mtu}`);
        }
        if (iface.disabled) {
          lines.push(`/interface set ${newName} disabled=yes`);
        } else if (iface.comment) {
          lines.push(`/interface set ${newName} comment="${iface.comment}"`);
        }
      });
      lines.push('');
    }
  }

  if (payload.bridges) {
    payload.bridges.forEach((bridge, i) => {
      lines.push(`# --- Bridge: ${bridge.name} ---`);
      let cmd = '/interface bridge add name=' + bridge.name;
      if (!bridge.stp_enabled) cmd += ' protocol=none';
      if (!bridge.auto_mac) cmd += ' auto-mac=no';
      if (bridge.mtu && bridge.mtu !== 1500) cmd += ' mtu=' + bridge.mtu;
      if (bridge.vlan_filtering) cmd += ' vlan-filtering=yes';
      cmd += ' disabled=no';
      lines.push(cmd);
      if (bridge.comment) lines.push('/interface bridge set ' + bridge.name + ' comment="' + bridge.comment + '"');

      (bridge.ports || []).forEach(port => {
        let pcmd = '/interface bridge port add bridge=' + bridge.name + ' interface=' + port;
        if (bridge.vlan_filtering) pcmd += ' pvid=' + (bridge.pvid || 1);
        pcmd += ' disabled=no';
        lines.push(pcmd);
      });

      // Bridge VLAN configuration
      if (bridge.vlan_filtering && bridge.vlans && bridge.vlans.length > 0) {
        lines.push('');
        lines.push('# --- Bridge VLAN Configuration ---');
        bridge.vlans.forEach(vlan => {
          let vcmd = '/interface bridge vlan add bridge=' + bridge.name + ' vlan-ids=' + (vlan.vlan_id || 10);
          if (vlan.tagged && vlan.tagged.length > 0) vcmd += ' tagged-ports=' + vlan.tagged.join(',');
          if (vlan.untagged && vlan.untagged.length > 0) vcmd += ' untagged-ports=' + vlan.untagged.join(',');
          if (vlan.comment) vcmd += ' comment="' + vlan.comment + '"';
          lines.push(vcmd);
        });
      }
      lines.push('');
    });
  }

  if (payload.vlans) {
    lines.push('# --- VLAN ---');
    payload.vlans.forEach(v => {
      lines.push(`/interface vlan add name=${v.name || 'vlan' + v.id} vlan-id=${v.id} interface=${v.parent_interface || 'bridge1'} disabled=no`);
      if (v.subnet) lines.push(`/ip address add address=${v.subnet} interface=${v.name || 'vlan' + v.id} disabled=no`);
    });
    lines.push('');
  }

  if (payload.dhcp_servers) {
    lines.push('# --- DHCP Server ---');
    payload.dhcp_servers.forEach((d, i) => {
      const name = d.name || 'dhcp' + (i + 1);
      lines.push(`/ip pool add name=${name}_pool ranges=${d.pool_start}-${d.pool_end}`);
      lines.push(`/ip dhcp-server network add address=${d.network} gateway=${d.gateway} dns-server=${d.dns}`);
      lines.push(`/ip dhcp-server add name=${name} interface=${d.interface} address-pool=${name}_pool disabled=no`);
    });
    lines.push('');
  }

  if (payload.port_forwards) {
    lines.push('# --- Port Forward ---');
    payload.port_forwards.forEach((pf, i) => {
      lines.push(`/ip firewall nat add chain=dstnat protocol=${pf.protocol || 'tcp'} dst-port=${pf.dst_port} action=dst-nat to-addresses=${pf.to_address} to-ports=${pf.to_port || pf.dst_port} comment="${pf.comment || 'PF-' + (i + 1)}"`);
    });
    lines.push('');
  }

  if (payload.firewall) {
    lines.push('# --- Firewall Filter Rules ---');
    lines.push('/ip firewall filter add chain=input connection-state=established,related action=accept comment="อนุญาตการเชื่อมต่อเดิม"');
    if (payload.firewall.drop_invalid) lines.push('/ip firewall filter add chain=input connection-state=invalid action=drop comment="บล็อกการเชื่อมต่อผิดปกติ"');
    lines.push('/ip firewall filter add chain=input protocol=icmp action=accept comment="อนุญาต Ping"');

    // Custom firewall rules
    if (payload.firewall.rules && payload.firewall.rules.length > 0) {
      payload.firewall.rules.forEach((rule, i) => {
        let cmd = '/ip firewall filter add';
        cmd += ' chain=' + (rule.chain || 'input');
        cmd += ' action=' + (rule.action || 'drop');
        if (rule.protocol) cmd += ' protocol=' + rule.protocol;
        if (rule.src_address) cmd += ' src-address=' + rule.src_address;
        if (rule.dst_address) cmd += ' dst-address=' + rule.dst_address;
        if (rule.dst_port) cmd += ' dst-port=' + rule.dst_port;
        if (rule.in_interface) cmd += ' in-interface=' + rule.in_interface;
        if (rule.out_interface) cmd += ' out-interface=' + rule.out_interface;
        if (rule.connection_state) cmd += ' connection-state=' + rule.connection_state;
        cmd += ' comment="' + rule.comment + '"';
        lines.push(cmd);
      });
    }

    if (payload.firewall.default_action === 'drop') lines.push('/ip firewall filter add chain=input action=drop comment="บล็อกทั้งหมด"');
    lines.push('');

    // NAT rules
    if (payload.firewall.nat_rules && payload.firewall.nat_rules.length > 0) {
      lines.push('# --- NAT Rules ---');
      payload.firewall.nat_rules.forEach((rule, i) => {
        let cmd = '/ip firewall nat add';
        cmd += ' chain=' + (rule.chain || 'dstnat');
        if (rule.protocol) cmd += ' protocol=' + rule.protocol;
        if (rule.dst_port) cmd += ' dst-port=' + rule.dst_port;
        if (rule.in_interface) cmd += ' in-interface=' + rule.in_interface;
        if (rule.out_interface) cmd += ' out-interface=' + rule.out_interface;
        if (rule.src_address) cmd += ' src-address=' + rule.src_address;
        if (rule.dst_address) cmd += ' dst-address=' + rule.dst_address;
        cmd += ' action=' + (rule.action || 'dst-nat');
        if (rule.to_addresses) cmd += ' to-addresses=' + rule.to_addresses;
        if (rule.to_ports) cmd += ' to-ports=' + rule.to_ports;
        cmd += ' comment="' + rule.comment + '"';
        lines.push(cmd);
      });
      lines.push('');
    }
  }

  if (payload.qos) {
    lines.push('# --- QoS ---');
    payload.qos.rules?.forEach((r, i) => {
      lines.push(`/queue simple add name=${r.name || 'queue-' + (i + 1)} target=${r.target} max-limit=${r.max_upload || '1M'}/${r.max_download || '1M'}`);
    });
    lines.push('');
  }

  if (payload.load_balance) {
    const lb = payload.load_balance;
    const wans = lb.wan_interfaces || [];
    if (wans.length > 0) {
      lines.push('# --- Load Balance / Failover ---');
      const method = lb.method || 'pcc';
      const rosVersion = 'v7';
      const recursiveFailover = lb.recursive_failover !== false;
      const pingInterval = lb.ping_interval || '10s';
      const pingTimeout = lb.ping_timeout || '5s';
      const pingFailureCount = lb.ping_failure_count || 3;

      // ROS v7: Create routing tables first
      lines.push('# ROS v7: Create routing tables');
      wans.forEach((wan, i) => {
        lines.push(`/routing table add name=wan${i + 1} fib`);
      });

      // Recursive Failover - Netwatch ping check
      if (recursiveFailover && wans.length > 1) {
        lines.push('');
        lines.push('# --- Recursive Failover (Ping Check via each WAN) ---');
        lines.push('# Netwatch pings external IP through each WAN');
        wans.forEach((wan, i) => {
          const gw = wan.gateway || '';
          if (!gw) return;
          const wanNum = i + 1;
          const pingTarget = wan.ping_target || '8.8.8.8';
          const routeMark = 'wan' + wanNum;
          lines.push('');
          lines.push(`# WAN-${wanNum} - Ping check via ${gw}`);
          lines.push(`/tool netwatch add host=${pingTarget} interval=${pingInterval} timeout=${pingTimeout} count=${pingFailureCount} routing-table=${routeMark} comment="Check-WAN-${wanNum}" on-up="/ip route set [find comment=\\"Route-WAN-${wanNum}\\"] disabled=no" on-down="/ip route set [find comment=\\"Route-WAN-${wanNum}\\"] disabled=yes"`);
          lines.push(`/ip route add dst-address=0.0.0.0/0 gateway=${gw}@main routing-table=${routeMark} distance=${method === 'failover' ? wanNum : 1} check-gateway=ping comment="Route-WAN-${wanNum}"`);
        });
      } else {
        // Standard routes
        lines.push('# Routes');
        wans.forEach((wan, i) => {
          const gw = wan.gateway || '';
          if (!gw) return;
          const routeMark = 'wan' + (i + 1);
          const dist = method === 'failover' ? (i + 1) : 1;
          let cmd = `/ip route add dst-address=0.0.0.0/0 gateway=${gw}@main routing-table=${routeMark} distance=${dist}`;
          if (lb.check_gateway) cmd += ' check-gateway=ping';
          lines.push(cmd);
        });
      }

      // Mangle - Mark connections (PCC only)
      if (method === 'pcc') {
        lines.push('');
        lines.push('# Mangle - PCC Mark Routing');
        wans.forEach((wan, i) => {
          const mark = 'wan' + (i + 1) + '_conn';
          const routeMark = 'wan' + (i + 1);
          const total = wans.length;
          lines.push(`/ip firewall mangle add chain=prerouting in-interface=bridge-lan action=mark-connection new-connection-mark=${mark} per-connection-classifier=both-addresses-and-ports:${total}/${i}`);
          lines.push(`/ip firewall mangle add chain=prerouting connection-mark=${mark} action=mark-routing new-routing-mark=${routeMark} passthrough=no`);
        });
      }

      // Main route
      lines.push('# Main Default Route');
      if (method === 'ecmp') {
        const gws = wans.filter(w => w.gateway).map(w => w.gateway + '@main');
        if (gws.length > 0) lines.push(`/ip route add dst-address=0.0.0.0/0 gateway=${gws.join(',')}`);
      } else if (method === 'failover') {
        wans.forEach((wan, i) => {
          if (wan.gateway) lines.push(`/ip route add dst-address=0.0.0.0/0 gateway=${wan.gateway}@main distance=${i + 1} check-gateway=ping`);
        });
      }

      // NAT per WAN
      lines.push('# NAT per WAN');
      wans.forEach((wan, i) => {
        const iface = wan.interface || ('ether' + (i + 1));
        lines.push(`/ip firewall nat add chain=srcnat out-interface=${iface} action=masquerade comment="NAT-WAN-${i + 1}"`);
      });

      lines.push('');
    }
  }

  if (payload.time_ntp) {
    const tn = payload.time_ntp;
    lines.push('# --- Time & NTP Configuration ---');
    if (tn.timezone) lines.push(`/system clock set time-zone-name=${tn.timezone}`);
    if (tn.ntp_enabled && tn.ntp_servers && tn.ntp_servers.length > 0) {
      lines.push(`/system ntp client set enabled=yes primary-ntp=${tn.ntp_servers[0]}`);
      if (tn.ntp_servers.length > 1) lines.push(`/system ntp client set secondary-ntp=${tn.ntp_servers[1]}`);
      if (tn.ntp_interval) lines.push(`/system ntp client set mode=unicast sync-interval=${tn.ntp_interval}`);
      if (tn.ntp_servers.length > 2) {
        tn.ntp_servers.slice(2).forEach((s, i) => {
          lines.push(`/system ntp server add address=${s}`);
        });
      }
    } else {
      lines.push('/system ntp client set enabled=no');
    }
    lines.push('');
  }

  if (payload.logging) {
    const log = payload.logging;
    lines.push('# --- Logging Configuration ---');

    // System logging
    if (log.local_disk) {
      lines.push('/system logging add topics=' + Object.keys(log.log_topics || {}).filter(k => log.log_topics[k]).join(',') + ' action=disk');
    }
    if (log.local_memory) {
      lines.push('/system logging add topics=' + Object.keys(log.log_topics || {}).filter(k => log.log_topics[k]).join(',') + ' action=memory memory-target-size=' + (log.memory_size || '10M'));
    }
    if (log.remote_enabled && log.remote_server) {
      lines.push('/system logging add topics=' + Object.keys(log.log_topics || {}).filter(k => log.log_topics[k]).join(',') + ' action=remote remote=' + log.remote_server + ':' + (log.remote_port || 514) + ' remote-protocol=' + (log.remote_protocol || 'udp'));
    }

    lines.push('');
  }

  if (payload.security) {
    const sec = payload.security;
    lines.push('# --- Security Hardening ---');

    // Disable services
    const svc = sec.disable_services || {};
    if (svc.api) lines.push('/ip service disable api');
    if (svc.ftp) lines.push('/ip service disable ftp');
    if (svc.telnet) lines.push('/ip service disable telnet');
    if (svc.www) lines.push('/ip service disable www');

    // Change ports
    const ports = sec.custom_ports || {};
    if (ports.winbox && ports.winbox !== 8291) lines.push('/ip service set winbox port=' + ports.winbox);
    if (ports.ssh && ports.ssh !== 22) lines.push('/ip service set ssh port=' + ports.ssh);
    if (ports.api && ports.api !== 8728) lines.push('/ip service set api port=' + ports.api);
    if (ports.www && ports.www !== 80) lines.push('/ip service set www port=' + ports.www);

    // Brute Force Protection
    if (sec.brute_force && sec.brute_force.enabled) {
      const bf = sec.brute_force;
      lines.push('');
      lines.push('# --- Brute Force Protection ---');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 src-address-list=ssh_blacklist action=drop comment="Block SSH Brute Force"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 src-address-list=ssh_stage3 action=add-src-to-address-list address-list=ssh_blacklist address-list-timeout=' + bf.ban_time + ' comment="Add to Blacklist"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 connection-state=new src-address-list=ssh_stage2 action=add-src-to-address-list address-list=ssh_stage3 address-list-timeout=' + bf.ban_time + ' comment="Stage 3"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 connection-state=new src-address-list=ssh_stage1 action=add-src-to-address-list address-list=ssh_stage2 address-list-timeout=' + bf.ban_time + ' comment="Stage 2"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 connection-state=new action=add-src-to-address-list address-list=ssh_stage1 address-list-timeout=' + bf.detect_time + ' comment="Stage 1"');
      if (bf.whitelist) lines.push('/ip firewall filter add chain=input src-address=' + bf.whitelist + ' action=accept comment="Whitelist"');
    }

    // Additional Hardening
    if (sec.hardening) {
      const h = sec.hardening;
      if (h.disable_macs_server) {
        lines.push('');
        lines.push('/ip neighbor discovery set [find] discover-interface-list=none');
        lines.push('/ip service set telnet disabled=yes');
      }
      if (h.disable_upnp) lines.push('/ip upnp set enabled=no');
      if (h.disable_cloud) lines.push('/ip cloud set update-time=no');
      if (h.disable_ipv6_firewall) {
        lines.push('/ipv6 firewall filter add chain=input action=drop comment="Block IPv6 Input"');
        lines.push('/ipv6 firewall filter add chain=forward action=drop comment="Block IPv6 Forward"');
      }
    }

    lines.push('');
  }

  if (payload.vpn && payload.vpn.enabled) {
    const vpn = payload.vpn;
    const s = vpn.server || {};
    lines.push('# --- VPN Server ---');

    if (s.type === 'l2tp') {
      lines.push('/ppp profile add name=vpn-profile local-address=' + (s.local_address || '10.10.10.1') + ' remote-address=' + (s.remote_address || '10.10.10.100-10.10.10.200') + ' dns-server=' + (s.dns_server || '8.8.8.8') + ' use-encryption=' + (s.use_encryption ? 'yes' : 'no'));
      lines.push('/interface l2tp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
      if (s.ipsec_secret) lines.push('/interface l2tp-server server set ipsec-secret=' + s.ipsec_secret);
    } else if (s.type === 'pptp') {
      lines.push('/ppp profile add name=vpn-profile local-address=' + (s.local_address || '10.10.10.1') + ' remote-address=' + (s.remote_address || '10.10.10.100-10.10.10.200') + ' dns-server=' + (s.dns_server || '8.8.8.8') + ' use-encryption=' + (s.use_encryption ? 'yes' : 'no'));
      lines.push('/interface pptp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    } else if (s.type === 'sstp') {
      lines.push('/ppp profile add name=vpn-profile local-address=' + (s.local_address || '10.10.10.1') + ' remote-address=' + (s.remote_address || '10.10.10.100-10.10.10.200') + ' dns-server=' + (s.dns_server || '8.8.8.8') + ' use-encryption=' + (s.use_encryption ? 'yes' : 'no'));
      lines.push('/interface sstp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    } else if (s.type === 'ovpn') {
      lines.push('/ppp profile add name=vpn-profile local-address=' + (s.local_address || '10.10.10.1') + ' remote-address=' + (s.remote_address || '10.10.10.100-10.10.10.200') + ' dns-server=' + (s.dns_server || '8.8.8.8') + ' use-encryption=' + (s.use_encryption ? 'yes' : 'no'));
      lines.push('/interface ovpn-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    }

    // VPN Users
    if (vpn.users && vpn.users.length > 0) {
      lines.push('');
      lines.push('# --- VPN Users ---');
      vpn.users.forEach(user => {
        lines.push('/ppp secret add name=' + user.name + ' password=' + user.password + ' service=' + (user.service || s.type || 'l2tp') + ' profile=vpn-profile');
      });
    }

    lines.push('');
  }

  if (payload.hotspot && payload.hotspot.enabled) {
    const hs = payload.hotspot;
    lines.push('# --- Hotspot Configuration ---');
    lines.push('/ip pool add name=hotspot_pool ranges=' + (hs.address_pool || '10.5.50.50-10.5.50.100'));
    lines.push('/ip dhcp-server add name=hs_dhcp interface=' + (hs.interface || 'bridge1') + ' address-pool=hotspot_pool lease-time=5m disabled=no');
    lines.push('/ip dhcp-server network add address=10.5.50.0/24 gateway=10.5.50.1 dns-server=' + (hs.dns_server || '8.8.8.8'));
    lines.push('/ip hotspot profile add name=hs_prof hotspot-address=10.5.50.1 dns-name=' + (hs.dns_name || 'hotspot.local') + ' login-by=http-chap,http-pap');
    lines.push('/ip hotspot user profile add name=default rate-limit=' + (hs.rate_limit || '5M/10M') + ' shared-users=' + (hs.shared_users || '1'));
    if (hs.default_user) lines.push('/ip hotspot user add name=' + hs.default_user + ' password=' + (hs.default_password || '1234') + ' profile=default');
    if (hs.walled_garden && hs.walled_garden.length > 0) {
      hs.walled_garden.forEach(wg => {
        lines.push('/ip hotspot walled-garden add dst-host=' + wg);
      });
    }
    lines.push('');
  }

  if (payload.logging) {
    const log = payload.logging;
    lines.push('# --- Logging Configuration ---');
    const topics = Object.keys(log.log_topics || {}).filter(k => log.log_topics[k]).join(',') || '!debug';
    if (log.local_disk) lines.push('/system logging add topics=' + topics + ' action=disk');
    if (log.local_memory) lines.push('/system logging add topics=' + topics + ' action=memory memory-target-size=' + (log.memory_size || '10M'));
    if (log.remote_enabled && log.remote_server) lines.push('/system logging add topics=' + topics + ' action=remote remote=' + log.remote_server + ':' + (log.remote_port || 514) + ' remote-protocol=' + (log.remote_protocol || 'udp'));
    lines.push('');
  }

  if (payload.security) {
    const sec = payload.security;
    lines.push('# --- Security Hardening ---');
    const svc = sec.disable_services || {};
    if (svc.api) lines.push('/ip service disable api');
    if (svc.ftp) lines.push('/ip service disable ftp');
    if (svc.telnet) lines.push('/ip service disable telnet');
    if (svc.www) lines.push('/ip service disable www');
    const ports = sec.custom_ports || {};
    if (ports.winbox && ports.winbox !== 8291) lines.push('/ip service set winbox port=' + ports.winbox);
    if (ports.ssh && ports.ssh !== 22) lines.push('/ip service set ssh port=' + ports.ssh);
    if (ports.api && ports.api !== 8728) lines.push('/ip service set api port=' + ports.api);
    if (ports.www && ports.www !== 80) lines.push('/ip service set www port=' + ports.www);
    if (sec.brute_force && sec.brute_force.enabled) {
      const bf = sec.brute_force;
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 connection-state=new action=add-src-to-address-list address-list=ssh_stage1 address-list-timeout=' + bf.detect_time + ' comment="BF Stage 1"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 connection-state=new src-address-list=ssh_stage1 action=add-src-to-address-list address-list=ssh_stage2 address-list-timeout=' + bf.ban_time + ' comment="BF Stage 2"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 src-address-list=ssh_stage2 action=add-src-to-address-list address-list=ssh_blacklist address-list-timeout=' + bf.ban_time + ' comment="BF Stage 3"');
      lines.push('/ip firewall filter add chain=input protocol=tcp dst-port=22,8291 src-address-list=ssh_blacklist action=drop comment="Block SSH Brute Force"');
      if (bf.whitelist) lines.push('/ip firewall filter add chain=input src-address=' + bf.whitelist + ' action=accept comment="BF Whitelist"');
    }
    const h = sec.hardening || {};
    if (h.disable_macs_server) lines.push('/ip neighbor discovery set [find] discover-interface-list=none');
    if (h.disable_upnp) lines.push('/ip upnp set enabled=no');
    if (h.disable_cloud) lines.push('/ip cloud set update-time=no');
    if (h.disable_ipv6_firewall) { lines.push('/ipv6 firewall filter add chain=input action=drop comment="Block IPv6 Input"'); lines.push('/ipv6 firewall filter add chain=forward action=drop comment="Block IPv6 Forward"'); }
    lines.push('');
  }

  if (payload.website_block && payload.website_block.enabled && payload.website_block.sites && payload.website_block.sites.length > 0) {
    lines.push('# --- Website Blocking ---');
    lines.push('/ip firewall layer7-protocol add name=blocked_sites regexp="' + payload.website_block.sites.map(s => s.replace(/\./g, '\\.')).join('|') + '"');
    lines.push('/ip firewall filter add chain=forward layer7-protocol=blocked_sites action=drop comment="บล็อกเว็บไซต์"');
    lines.push('');
  }

  if (payload.vpn && payload.vpn.enabled) {
    const vpn = payload.vpn;
    const s = vpn.server || {};
    lines.push('# --- VPN Server ---');
    lines.push('/ppp profile add name=vpn-profile local-address=' + (s.local_address || '10.10.10.1') + ' remote-address=' + (s.remote_address || '10.10.10.100-10.10.10.200') + ' dns-server=' + (s.dns_server || '8.8.8.8') + ' use-encryption=' + (s.use_encryption ? 'yes' : 'no'));
    if (s.type === 'l2tp') {
      lines.push('/interface l2tp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
      if (s.ipsec_secret) lines.push('/interface l2tp-server server set ipsec-secret=' + s.ipsec_secret);
    } else if (s.type === 'pptp') {
      lines.push('/interface pptp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    } else if (s.type === 'sstp') {
      lines.push('/interface sstp-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    } else if (s.type === 'ovpn') {
      lines.push('/interface ovpn-server server set enabled=yes max-mtu=' + (s.mtu || 1450) + ' max-mru=' + (s.mru || 1450) + ' keepalive-timeout=' + (s.keepalive_timeout || '30s') + ' default-profile=vpn-profile');
    }
    if (vpn.users && vpn.users.length > 0) {
      lines.push('');
      lines.push('# --- VPN Users ---');
      vpn.users.forEach(user => {
        lines.push('/ppp secret add name=' + user.name + ' password=' + user.password + ' service=' + (user.service || s.type || 'l2tp') + ' profile=vpn-profile');
      });
    }
    lines.push('');
  }

  lines.push('# ============================================================');
  lines.push('# จบการตั้งค่า');
  lines.push('# ============================================================');
  return lines.join('\n');
}
